import { useMsal } from "@azure/msal-react";
import { getToken, useMsalAuth } from "../../authConfig";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { HistoryData, HistoryItem } from "../HistoryItem";
import { HistoryProviderOptions } from "../HistoryProviders/IProvider";
import { useHistoryManager, HistoryMetaData } from "../HistoryProviders";
import { useTranslation } from "react-i18next";
import styles from "./HistoryPanel.module.css";
import { useNavigate, useParams } from "react-router-dom";
import { LoginContext } from "../../loginContext";
import { useProjects } from "../../contexts/ProjectContext";
import { Spinner } from "../ui";

const HISTORY_COUNT_PER_LOAD = 30;

export const HistoryPanel = ({ provider, notify, showTitle = true }: { provider: HistoryProviderOptions; notify: boolean; showTitle?: boolean }) => {
    const params = useParams<{ chatId?: string }>();
    const { chatId: urlChatId } = params;
    const historyManager = useHistoryManager(provider);

    const [chatId, setChatId] = useState<string | undefined>(urlChatId);
    const [history, setHistory] = useState<HistoryMetaData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMoreHistory, setHasMoreHistory] = useState(false);
    const [initializedAfterLogin, setInitializedAfterLogin] = useState(false);
    const [reloadedAfterNewChatCreation, setReloadedAfterNewChatCreation] = useState(true);
    const { loggedIn } = useContext(LoginContext);

    const client = useMsalAuth ? useMsal().instance : undefined;
    const navigate = useNavigate();
    const { getAllProjectChatIds } = useProjects();

    useEffect(() => {
        if (!urlChatId) {
            setChatId(undefined);
            setReloadedAfterNewChatCreation(false);
            return;
        }

        setChatId(urlChatId);
        if (!reloadedAfterNewChatCreation) {
            resetHistory();
            setReloadedAfterNewChatCreation(true);
        }
    }, [urlChatId]);

    useEffect(() => {
        if (loggedIn && !initializedAfterLogin) {
            resetHistory();
            setInitializedAfterLogin(true);
        }
    }, [loggedIn, initializedAfterLogin]);

    useEffect(() => {
        if (notify) {
            resetHistory();
        }
    }, [notify]);

    const resetHistory = () => {
        setHistory([]);
        historyManager.resetContinuationToken();
        setHasMoreHistory(true);
    };

    const loadMoreHistory = async () => {
        setIsLoading(() => true);
        const token = client ? await getToken(client) : undefined;
        const items = await historyManager.getNextItems(HISTORY_COUNT_PER_LOAD, token);

        if (items.length === 0) {
            setHasMoreHistory(false);
        }
        setHistory(prevHistory => [...prevHistory, ...items]);
        setIsLoading(() => false);
    };

    const handleSelect = (id: string) => {
        // Find the session to check if it's an agent session
        const session = history.find(item => item.id === id);
        if (session?.agent_type) {
            // Map agent_type to the correct route segment
            const agentTypeToRoute: Record<string, string> = {
                scheduling: "scheduling",
                scheduling_member: "scheduling",
                scheduling_admin: "scheduling",
                law: "law",
                quality: "quality"
            };
            const routeSegment = agentTypeToRoute[session.agent_type] ?? session.agent_type;
            navigate(`/app/agent/${routeSegment}/${id}`);
        } else {
            // This is a regular chat session
            navigate(`/app/chats/${id}`);
        }
    };

    const handleDelete = async (id: string) => {
        const token = client ? await getToken(client) : undefined;
        await historyManager.deleteItem(id, token);
        setHistory(prevHistory => prevHistory.filter(item => item.id !== id));

        if (id === chatId) {
            setChatId(undefined);
            navigate(`/app`);
        }
    };

    // Filter out project-based chats at render time (ensures filtering works even if projects load after history)
    const groupedHistory = useMemo(() => {
        const projectChatIds = getAllProjectChatIds();
        const filteredHistory = history.filter(item => !projectChatIds.includes(item.id));
        return groupHistory(filteredHistory);
    }, [history, getAllProjectChatIds]);

    const { t } = useTranslation();

    return (
        <div className={styles.historyPanel}>
            {showTitle && <h4 className={styles.historyTitle}>{t("history.chatHistory")}</h4>}
            <div>
                {Object.entries(groupedHistory).map(([group, items]) => (
                    <div key={group} className={styles.group}>
                        <p className={styles.groupLabel}>{t(group)}</p>
                        {items.map(item => (
                            <HistoryItem key={item.id} isActive={item.id === chatId} item={item} onSelect={handleSelect} onDelete={handleDelete} />
                        ))}
                    </div>
                ))}
                {isLoading && (
                    <div className={styles.loadingState}>
                        <Spinner size="sm" />
                    </div>
                )}
                {history.length === 0 && !isLoading && <p className={styles.noHistory}>{t("history.noHistory")}</p>}
                {hasMoreHistory && !isLoading && <InfiniteLoadingButton func={loadMoreHistory} />}
            </div>
        </div>
    );
};

function groupHistory(history: HistoryData[]) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setDate(lastMonth.getDate() - 30);

    return history.reduce(
        (groups, item) => {
            const itemDate = new Date(item.timestamp);
            let group;

            if (itemDate >= today) {
                group = "history.today";
            } else if (itemDate >= yesterday) {
                group = "history.yesterday";
            } else if (itemDate >= lastWeek) {
                group = "history.last7days";
            } else if (itemDate >= lastMonth) {
                group = "history.last30days";
            } else {
                group = itemDate.toLocaleDateString(undefined, { year: "numeric", month: "long" });
            }

            if (!groups[group]) {
                groups[group] = [];
            }
            groups[group].push(item);
            return groups;
        },
        {} as Record<string, HistoryData[]>
    );
}

const InfiniteLoadingButton = ({ func }: { func: () => void }) => {
    const triggerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        if (triggerRef.current) {
                            func();
                        }
                    }
                });
            },
            {
                root: null,
                threshold: 0
            }
        );

        if (triggerRef.current) {
            observer.observe(triggerRef.current);
        }

        return () => {
            if (triggerRef.current) {
                observer.unobserve(triggerRef.current);
            }
        };
    }, []);

    return <div ref={triggerRef} className={styles.loadMoreTrigger} aria-hidden="true" />;
};
